import { useCallback, useState } from "react";

import { startLatencySpan } from "@/lib/session-latency-trace";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import { track } from "./observability";
import { SCREEN_RECORDING_PERMISSION_NEEDED_EVENT } from "./screen-recording-permission-event";

interface CapturedScreen {
  index: number;
  displayId: number;
  isCursorScreen: boolean;
  label: string;
  widthPx: number;
  heightPx: number;
  scaledWidth: number;
  scaledHeight: number;
  scaleFactor: number;
  originX: number;
  originY: number;
  widthPoints?: number;
  heightPoints?: number;
  jpegBase64: string;
  previewJpegBase64: string;
}
interface ScreenshotResult {
  cursorX: number;
  cursorY: number;
  screens: CapturedScreen[];
}

interface AreaScreenshotResult {
  displayId: number;
  x: number;
  y: number;
  width: number;
  height: number;
  widthPx: number;
  heightPx: number;
  scaledWidth: number;
  scaledHeight: number;
  scaleFactor: number;
  originX: number;
  originY: number;
  widthPoints?: number;
  heightPoints?: number;
  jpegBase64: string;
}

/** Capture output: the JPEG `File` objects the caller uploads, plus
 *  the per-screen metadata required by `resolveAgentPoint` to invert
 *  any subsequent `[POINT:screen=N,x=X,y=Y]` reply back to global
 *  desktop coords. The two arrays are index-aligned.
 *
 *  `cursorX` / `cursorY` are the global desktop coordinates (OS pixels)
 *  of the cursor at capture time. They flow through to the
 *  `[POINTER:...]` token attached to the user message in the desktop
 *  overlay so the agent knows what the user was pointing at. */
export interface CaptureOutput {
  files: File[];
  screens: ScreenshotScreenMeta[];
  previews: ScreenshotPreview[];
  cursorX: number;
  cursorY: number;
}

export interface ScreenshotPreview {
  displayId: number;
  jpegBase64: string;
}

export interface CaptureAreaInput {
  displayId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CaptureAreaOutput {
  file: File | null;
  result: AreaScreenshotResult | null;
}

export interface CaptureDisplayOutput {
  file: File | null;
  screen: ScreenshotScreenMeta | null;
  preview: ScreenshotPreview | null;
  cursorX: number;
  cursorY: number;
}

export type CaptureScope = "cursor_screen" | "all";
export type CaptureQuality = "standard" | "precision";

interface CaptureOptions {
  scope?: CaptureScope;
  quality?: CaptureQuality;
}

function base64ToFile(b64: string, name: string): File {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: "image/jpeg" });
}

function isTauriRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return Boolean(w.__TAURI_INTERNALS__);
}

function toMeta(s: CapturedScreen): ScreenshotScreenMeta {
  return {
    index: s.index,
    displayId: s.displayId,
    isCursorScreen: s.isCursorScreen,
    scaleFactor: s.scaleFactor,
    originX: s.originX,
    originY: s.originY,
    ...(typeof s.widthPoints === "number"
      ? { widthPoints: s.widthPoints }
      : {}),
    ...(typeof s.heightPoints === "number"
      ? { heightPoints: s.heightPoints }
      : {}),
    widthPx: s.widthPx,
    heightPx: s.heightPx,
    scaledWidth: s.scaledWidth,
    scaledHeight: s.scaledHeight,
  };
}

function emitScreenRecordingPermissionNeeded() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SCREEN_RECORDING_PERMISSION_NEEDED_EVENT));
}

type ScreenRecordingGrant = "granted" | "denied" | "unknown";

/** Reads the grant without asking for it. "unknown" when older dev builds
 *  lack the permission command, or the helper could not check it yet. */
async function readScreenRecordingGrant(
  invoke: <T>(command: string) => Promise<T>,
): Promise<ScreenRecordingGrant> {
  try {
    const status = await invoke<{ screenRecording?: string }>(
      "permissions_check_screen_recording",
    );
    if (status?.screenRecording === "unknown") return "unknown";
    return status?.screenRecording === "granted" ? "granted" : "denied";
  } catch {
    return "unknown";
  }
}

async function ensureScreenRecordingPermission(
  invoke: <T>(command: string) => Promise<T>,
): Promise<boolean> {
  const finish = startLatencySpan("screenshot.permission");
  const grant = await readScreenRecordingGrant(invoke);
  finish({
    outcome:
      grant === "granted" ? "success" : grant === "denied" ? "denied" : "error",
  });
  if (grant === "denied") emitScreenRecordingPermissionNeeded();
  // An unknown grant lets the capture command surface the real failure.
  return grant !== "denied";
}

/** Whether Screen Recording is already granted, checked silently: never
 *  opens the permission UI. False when it cannot be told. */
export async function isScreenRecordingGranted(): Promise<boolean> {
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return (await readScreenRecordingGrant(invoke)) === "granted";
  } catch {
    return false;
  }
}

async function traceCaptureCommand<T>(
  capture: () => Promise<T>,
  scope: string,
  quality: CaptureQuality,
): Promise<T> {
  const finish = startLatencySpan("screenshot.command", { scope, quality });
  let outcome = "error";
  try {
    const result = await capture();
    outcome = "success";
    return result;
  } finally {
    finish({ outcome });
  }
}

/** Non-hook helper for callers outside React (Tauri event listeners,
 *  zustand actions). Returns empty files, screens, and previews
 *  outside Tauri.
 *
 *  Default scope is `"cursor_screen"`: only the display containing the
 *  cursor is captured. Pass `{ scope: "all" }` to fan out to every
 *  connected display (legacy behaviour). */
export async function captureScreenshotsAsFiles(
  options: CaptureOptions = {},
): Promise<CaptureOutput> {
  if (!isTauriRuntime()) {
    return { files: [], screens: [], previews: [], cursorX: 0, cursorY: 0 };
  }
  const { invoke } = await import("@tauri-apps/api/core");
  const scope: CaptureScope = options.scope ?? "cursor_screen";
  const quality: CaptureQuality =
    options.quality ?? (scope === "cursor_screen" ? "precision" : "standard");
  if (!(await ensureScreenRecordingPermission(invoke))) {
    return { files: [], screens: [], previews: [], cursorX: 0, cursorY: 0 };
  }
  track("desktop_screenshot_requested", { scope, quality });
  const startedAt = Date.now();
  const command =
    scope === "all"
      ? "screenshot_capture_all"
      : "screenshot_capture_cursor_screen";
  try {
    const result = await traceCaptureCommand(
      () =>
        invoke<ScreenshotResult>(
          command,
          quality === "precision" ? { precision: true } : undefined,
        ),
      scope,
      quality,
    );
    // Sidecar enumeration order is not stable for non-cursor displays. Give
    // the model one deterministic desktop order and re-index both aligned
    // arrays together so `screen=N` always means the same image.
    const orderedScreens = [...result.screens]
      .sort(
        (a, b) =>
          a.originY - b.originY ||
          a.originX - b.originX ||
          a.displayId - b.displayId,
      )
      .map((screen, index) => ({ ...screen, index }));
    const total = orderedScreens.length;
    const files = orderedScreens.map((s) => {
      const cursorTag = s.isCursorScreen ? "-cursor" : "";
      const name = `screen-${s.index + 1}-of-${total}${cursorTag}.jpg`;
      return base64ToFile(s.jpegBase64, name);
    });
    const screens = orderedScreens.map(toMeta);
    const previews = orderedScreens.flatMap((screen) =>
      screen.previewJpegBase64
        ? [
            {
              displayId: screen.displayId,
              jpegBase64: screen.previewJpegBase64,
            },
          ]
        : [],
    );
    const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
    track("desktop_screenshot_succeeded", {
      scope,
      quality,
      display_count: total,
      total_bytes: totalBytes,
      latency_ms: Date.now() - startedAt,
    });
    return {
      files,
      screens,
      previews,
      cursorX: result.cursorX,
      cursorY: result.cursorY,
    };
  } catch (e) {
    track("desktop_screenshot_failed", {
      scope,
      quality,
      latency_ms: Date.now() - startedAt,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    emitScreenRecordingPermissionNeeded();
    throw e;
  }
}

/** Captures exactly one macOS display by its stable CoreGraphics display ID.
 *  The native response is checked before returning so callers can safely bind
 *  the file and metadata to one view chapter without relying on cursor state.
 *  `onCommandStart` runs right before the capture command is sent, after the
 *  permission check. */
export async function captureScreenshotDisplayAsFile(
  displayId: number,
  onCommandStart?: () => void,
): Promise<CaptureDisplayOutput> {
  if (!Number.isInteger(displayId) || displayId < 0 || displayId > 0xffffffff) {
    throw new Error(`invalid display id: ${displayId}`);
  }
  if (!isTauriRuntime()) {
    return {
      file: null,
      screen: null,
      preview: null,
      cursorX: 0,
      cursorY: 0,
    };
  }
  const { invoke } = await import("@tauri-apps/api/core");
  if (!(await ensureScreenRecordingPermission(invoke))) {
    return {
      file: null,
      screen: null,
      preview: null,
      cursorX: 0,
      cursorY: 0,
    };
  }
  track("desktop_screenshot_requested", {
    scope: "display",
    quality: "standard",
    display_id: displayId,
  });
  const startedAt = Date.now();
  try {
    onCommandStart?.();
    const result = await traceCaptureCommand(
      () =>
        invoke<ScreenshotResult>("screenshot_capture_display", { displayId }),
      "display",
      "standard",
    );
    const captured = result.screens[0];
    if (result.screens.length !== 1 || captured?.displayId !== displayId) {
      throw new Error(`screenshot response did not match display ${displayId}`);
    }
    const screen = toMeta({ ...captured, index: 0 });
    const file = base64ToFile(
      captured.jpegBase64,
      `screen-display-${displayId}.jpg`,
    );
    track("desktop_screenshot_succeeded", {
      scope: "display",
      quality: "standard",
      display_id: displayId,
      display_count: 1,
      total_bytes: file.size,
      latency_ms: Date.now() - startedAt,
    });
    return {
      file,
      screen,
      preview: captured.previewJpegBase64
        ? {
            displayId: captured.displayId,
            jpegBase64: captured.previewJpegBase64,
          }
        : null,
      cursorX: result.cursorX,
      cursorY: result.cursorY,
    };
  } catch (e) {
    track("desktop_screenshot_failed", {
      scope: "display",
      quality: "standard",
      display_id: displayId,
      latency_ms: Date.now() - startedAt,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    emitScreenRecordingPermissionNeeded();
    throw e;
  }
}

export async function captureScreenshotAreaAsFile(
  area: CaptureAreaInput,
): Promise<CaptureAreaOutput> {
  if (!isTauriRuntime()) {
    return { file: null, result: null };
  }
  const { invoke } = await import("@tauri-apps/api/core");
  if (!(await ensureScreenRecordingPermission(invoke))) {
    return { file: null, result: null };
  }
  track("desktop_screenshot_requested", {
    scope: "area",
    quality: "precision",
  });
  const startedAt = Date.now();
  try {
    const result = await traceCaptureCommand(
      () =>
        invoke<AreaScreenshotResult>("screenshot_capture_area", {
          area: {
            display_id: area.displayId,
            x: area.x,
            y: area.y,
            width: area.width,
            height: area.height,
          },
        }),
      "area",
      "precision",
    );
    const file = base64ToFile(
      result.jpegBase64,
      `screen-${result.displayId}-area.jpg`,
    );
    track("desktop_screenshot_succeeded", {
      scope: "area",
      quality: "precision",
      display_count: 1,
      total_bytes: file.size,
      latency_ms: Date.now() - startedAt,
    });
    return { file, result };
  } catch (e) {
    track("desktop_screenshot_failed", {
      scope: "area",
      quality: "precision",
      latency_ms: Date.now() - startedAt,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    emitScreenRecordingPermissionNeeded();
    throw e;
  }
}

export function useScreenshotCapture() {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const captureNow = useCallback(
    async (options: CaptureOptions = {}): Promise<CaptureOutput> => {
      if (!isTauriRuntime()) {
        return {
          files: [],
          screens: [],
          previews: [],
          cursorX: 0,
          cursorY: 0,
        };
      }
      setCapturing(true);
      setError(null);
      try {
        return await captureScreenshotsAsFiles(options);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return {
          files: [],
          screens: [],
          previews: [],
          cursorX: 0,
          cursorY: 0,
        };
      } finally {
        setCapturing(false);
      }
    },
    [],
  );

  return { captureNow, capturing, error };
}
